import React, { useEffect, useState } from 'react';

import Heading from '@atlaskit/heading';
import ImageIcon from '@atlaskit/icon/core/image';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { CloseButton, type OnCloseHandler } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { SimpleTag as Tag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import { token } from '@atlaskit/tokens';

import { getComponentName } from '../get-component-name';
import { getIcon } from '../helpers';
import type { NewCoreIconData } from '../types';

import CodeCopyToClipboard from './code-copy-to-clipboard';
import CodeIconCopyToClipboard from './code-icon-copy-to-clipboard';

export type IconDetailsProps = BaseIconDetailsProps;

export type BaseIconDetailsProps = {
	icon?: NewCoreIconData;
	testId?: string;
	isModal?: boolean;
	onClose?: OnCloseHandler;
	replacementIcon?: NewCoreIconData;
};

const boxPanelStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
	borderWidth: 'border.width',
	width: '336px',
});

const closeButtonStyles = xcss({
	position: 'absolute',
	insetInlineEnd: 'space.200',
	insetBlockStart: 'space.200',
});

const sharedBoxBorderStyles = xcss({
	borderBlockEndColor: 'color.border',
	borderBlockEndStyle: 'solid',
	borderWidth: 'border.width',
});

const getCategorizationTagColor = (
	categorization: NewCoreIconData['categorization'],
	isDeprecated: boolean,
) => {
	if (isDeprecated) {
		return 'redLight';
	} else if (categorization === 'multi-purpose') {
		return 'limeLight';
	} else if (categorization === 'single-purpose') {
		return 'purpleLight';
	} else if (categorization === 'utility') {
		return 'magentaLight';
	}
};

const getCategorizationTagText = (
	categorization: NewCoreIconData['categorization'],
	isDeprecated: boolean,
) => {
	if (isDeprecated) {
		return 'Deprecated';
	} else if (categorization === 'multi-purpose') {
		return 'Multi purpose';
	} else if (categorization === 'single-purpose') {
		return 'Single purpose';
	} else if (categorization === 'utility') {
		return 'Utility';
	} else {
		return categorization;
	}
};

const getUsageText = (usage: string) => {
	const singlePurposePrefix = 'Single purpose - ';
	const multiPurposePrefix = 'Multi purpose - ';

	if (usage.startsWith(singlePurposePrefix)) {
		return usage.slice(singlePurposePrefix.length);
	} else if (usage.startsWith(multiPurposePrefix)) {
		return usage.slice(multiPurposePrefix.length);
	} else {
		return usage;
	}
};

/**
 * __Icon details__
 *
 * A panel which is populated when an icon is selected.
 * Contains information about the icon. *
 */
export const IconDetails = ({
	icon,
	testId,
	isModal,
	onClose,
}: IconDetailsProps): React.JSX.Element => {
	const [replacementIcon, setReplacementIcon] = useState<NewCoreIconData>();

	useEffect(() => {
		if (icon?.replacement) {
			const iconPromise = getIcon(icon.replacement.location, icon.replacement.name);

			if (iconPromise) {
				iconPromise.then((icon) => {
					setReplacementIcon(icon);
				});
			}
		}
	}, [icon]);

	return (
		<BaseIconDetails
			icon={icon}
			testId={testId}
			isModal={isModal}
			onClose={onClose}
			replacementIcon={replacementIcon}
		/>
	);
};

const RecommendedLozenge = <Lozenge>Recommended</Lozenge>;

export function BaseIconDetails({
	icon,
	testId,
	isModal,
	onClose,
	replacementIcon,
}: BaseIconDetailsProps): React.JSX.Element {
	const isDeprecated = icon?.status === 'deprecated';
	return icon ? (
		<Box xcss={!isModal && boxPanelStyles}>
			<Stack testId={testId} grow="fill">
				<Box
					xcss={sharedBoxBorderStyles}
					backgroundColor="elevation.surface.sunken"
					paddingInline="space.600"
					paddingBlock="space.300"
				>
					<Inline alignInline="center">
						<icon.component label="" />
					</Inline>
					{isModal && onClose && (
						<Box xcss={closeButtonStyles}>
							<CloseButton onClick={onClose} />
						</Box>
					)}
				</Box>
				<Box xcss={sharedBoxBorderStyles} padding="space.300">
					<Stack space="space.150">
						<Inline alignBlock="center" space="space.050">
							<Heading size="medium">{icon.iconName}</Heading>
							<Box>
								<Tag
									text={getCategorizationTagText(icon.categorization, isDeprecated)}
									color={getCategorizationTagColor(icon.categorization, isDeprecated)}
								/>
							</Box>
						</Inline>
						{icon.replacement && (
							<Stack space="space.150">
								<Text>This icon has been replaced by:</Text>
								{replacementIcon && (
									<CodeIconCopyToClipboard
										name={icon.replacement.name}
										icon={replacementIcon.component}
										value={`import ${getComponentName(icon.replacement.name)} from '${
											icon.replacement.location
										}/core/${icon.replacement.name}';`}
										prompt="Copy replacement code"
									/>
								)}
							</Stack>
						)}
						{icon.usage && <Text>{getUsageText(icon.usage)}</Text>}
						<TagGroup label="keywords" alignment="start">
							{icon.keywords.map((keyword) => (
								<Tag text={keyword} />
							))}
						</TagGroup>
						<Stack space="space.100">
							<Heading size="small">Maintained by</Heading>
							<Text>{icon.team}</Text>
						</Stack>
					</Stack>
				</Box>
				<Box xcss={sharedBoxBorderStyles} padding="space.300">
					<Stack space="space.150">
						<Heading size="small">React</Heading>
						<CodeCopyToClipboard
							messages={{
								prompt: 'Copy import',
								success: 'Copied!',
								fail: 'Copy failed',
							}}
							value={`import ${icon.componentName} from '${icon.packageName}';`}
						>
							import {icon.componentName} from '{icon.packageName}';
						</CodeCopyToClipboard>
						<Link href="/components/icon/examples" target="_blank">
							Icon code examples
						</Link>
					</Stack>
				</Box>

				<Box xcss={sharedBoxBorderStyles} padding="space.300">
					<Stack space="space.150">
						<Heading size="small">Sizes</Heading>
						<Stack space="space.0">
							<CodeIconCopyToClipboard
								key={'small'}
								name={'Small'}
								icon={() => <icon.component label="" size="small" spacing="spacious" />}
								suffixElement={icon.shouldRecommendSmallIcon && RecommendedLozenge}
								value={`<${icon.componentName} label="" size="small" />`}
								prompt={'Copy usage code'}
							/>
							<Stack space="space.100">
								<CodeIconCopyToClipboard
									key={'medium'}
									name={'Medium'}
									icon={() => <icon.component label="" spacing="spacious" />}
									suffixElement={!icon.shouldRecommendSmallIcon && RecommendedLozenge}
									value={`<${icon.componentName} label="" />`}
									prompt={'Copy usage code'}
								/>
								{icon.shouldRecommendSmallIcon && (
									<SectionMessage appearance="warning">
										<Text>
											Atlassian uses small (12px) chevrons within buttons and dropdowns to denote
											expand/collapse functionality. We recommend using small (12px) chevrons to
											maintain uniformity across Atlassian experiences.
										</Text>
									</SectionMessage>
								)}
							</Stack>
						</Stack>
					</Stack>
				</Box>
				<Box padding="space.300">
					<Stack space="space.150">
						<Heading size="small">Figma</Heading>
						<Link href="https://go.atlassian.com/ads-icons-figma" target="_blank">
							Atlassian icon library (Atlassians only)
						</Link>
					</Stack>
				</Box>
			</Stack>
		</Box>
	) : (
		<Box
			xcss={boxPanelStyles}
			backgroundColor="elevation.surface.sunken"
			paddingInline="space.600"
			paddingBlock="space.300"
		>
			<Stack space="space.150" testId={testId} alignInline="center" grow="fill">
				<ImageIcon label="" color="currentColor" />
				<Text color="color.text.subtle" align="center">
					Select an icon for details
				</Text>
			</Stack>
		</Box>
	);
}
